import React, { useRef, useState } from "react";
import { Formik, Field, Form } from "formik";
import SimpleReactValidator from "simple-react-validator";
import Select from "react-select";
import {
  VendorChevronDownSvg,
  VendorCloseSvg,
  VendorDropzoneSvg,
  VendorFileSvg,
  VendorFolderSvg,
  VendorUploadSvg,
} from "../../../components/CommonSvg";
import { __t } from "../../../i18n/translator";
import { fileKind } from "./documentUtils";

const MAX_FILE_MB = 10;
const MAX_REMARK = 500;
const ALL_VENDORS_OPTION = [{ value: "all", label: "All Vendors" }];

const ACCEPTED =
  ".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.gif,.zip";

const fileSize = (bytes) => {
  const kb = Math.max(1, Math.round((bytes || 0) / 1024));
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
};

const UploadDocument = ({
  categories = [],
  uploading = false,
  defaultCategory = "",
  isAdmin = false,
  vendors = [],
  onSubmit,
  onCancel,
}) => {
  const fileInput = useRef(null);
  const validator = useRef(new SimpleReactValidator());
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [titleTouched, setTitleTouched] = useState(false);
  const [fileError, setFileError] = useState("");
  /* Nothing is chosen to begin with. Defaulting to every vendor made the
     widest possible audience the one you get by not deciding, and submit
     already refuses an empty picker - so the choice has to be made. */
  const [allVendors, setAllVendors] = useState(false);
  const [pickedVendors, setPickedVendors] = useState([]);
  const [vendorError, setVendorError] = useState("");

  const initialValues = {
    documentTitle: "",
    category: defaultCategory || categories[0]?.id || "",
    email: "",
    remark: "",
  };

  const takeFile = (picked, setFieldValue) => {
    if (!picked) return;
    if (picked.size > MAX_FILE_MB * 1024 * 1024) {
      setFileError(`${picked.name} is larger than ${MAX_FILE_MB} MB`);
      return;
    }
    setFileError("");
    setFile(picked);
    /* the filename is the title nine times out of ten - offered, not imposed,
       so anything the user has already typed is left alone */
    if (!titleTouched) {
      setFieldValue("documentTitle", picked.name.replace(/\.[^.]+$/, ""));
    }
  };

  const handleSubmit = (values) => {
    if (!file) {
      setFileError("Please choose a file to upload");
      return;
    }
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      return;
    }
    if (isAdmin && !allVendors && pickedVendors.length === 0) {
      setVendorError("Please choose which vendors can see this document");
      return;
    }

    const formData = new FormData();
    formData.append("document", file, file.name);
    formData.append("documentTitle", values.documentTitle.trim());
    formData.append("category", values.category);
    formData.append("email", values.email.trim());
    formData.append("remarks", values.remark.trim());
    if (isAdmin) {
      formData.append("vendorIds", allVendors ? "all" : pickedVendors.join(","));
    }

    setFileError("");
    setVendorError("");
    onSubmit(formData);
  };

  const kind = file ? fileKind(file.name) : null;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values, setFieldValue }) => (
        <Form className="vp-up-form">
          <div className="vp-up-field">
            <label className="vp-up-label" htmlFor="documentTitle">
              Document Title<span className="req">*</span>
            </label>
            <Field
              id="documentTitle"
              name="documentTitle"
              type="text"
              className="vp-up-input"
              placeholder="Enter document title"
              onKeyUp={() => setTitleTouched(true)}
            />
            {validator.current.message(
              __t("validations.document.title"),
              values.documentTitle,
              "required",
              {
                messages: { required: __t("validations.document.title") },
              }
            )}
          </div>

          <div className="vp-up-field">
            <label className="vp-up-label" htmlFor="vp-up-file">
              Upload File<span className="req">*</span>
            </label>

            {/* once a file is chosen the zone steps aside for it - keeping a
                dropzone the size of a card above the picked file gave the step
                that was already done more of the dialog than the ones left */}
            {file ? (
              <div className="vp-up-picked">
                <span className={`vp-up-pico ${kind.cls}`}>
                  <VendorFileSvg size={34} />
                  <i>{kind.label}</i>
                </span>
                <span className="vp-up-pbody">
                  <b title={file.name}>{file.name}</b>
                  <i>
                    {kind.type} · {fileSize(file.size)}
                  </i>
                </span>
                <button
                  type="button"
                  className="vp-up-px"
                  onClick={() => {
                    setFile(null);
                    setFileError("");
                  }}
                  title={`Remove ${file.name}`}
                >
                  <VendorCloseSvg size={14} />
                </button>
              </div>
            ) : (
              <label
                className={`vp-up-dz${dragging ? " drag" : ""}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  takeFile(event.dataTransfer.files?.[0], setFieldValue);
                }}
              >
                <span className="vp-up-dzicon">
                  <VendorDropzoneSvg size={22} />
                </span>
                <span className="vp-up-dztext">
                  <span className="vp-up-dzmain">
                    Drag &amp; drop your file here
                  </span>
                  <span className="vp-up-dzor">
                    or click to browse from your device
                  </span>
                  <span className="vp-up-dzsub">
                    PDF, Word, Excel, PowerPoint, Images or Zip · Up to{" "}
                    {MAX_FILE_MB} MB
                  </span>
                </span>
                {/* the whole zone is the label, so this is only something to
                    aim at - the click it takes is the label's either way */}
                <span className="vp-up-dzbtn">
                  <VendorFolderSvg size={15} />
                  Browse Files
                </span>
                <input
                  id="vp-up-file"
                  ref={fileInput}
                  type="file"
                  accept={ACCEPTED}
                  onChange={(event) => {
                    takeFile(event.target.files?.[0], setFieldValue);
                    /* clearing lets the same file be picked again after it
                       was removed */
                    event.target.value = "";
                  }}
                />
              </label>
            )}

            {fileError && <div className="vp-up-err">{fileError}</div>}
          </div>

          <div className="vp-up-field">
            <label className="vp-up-label" htmlFor="category">
              Folder<span className="req">*</span>
            </label>
            <div className="vp-up-select">
              <span className="vp-up-sicon">
                <VendorFolderSvg size={16} />
              </span>
              <Field as="select" id="category" name="category">
                {categories.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Field>
              <span className="vp-up-scaret">
                <VendorChevronDownSvg />
              </span>
            </div>
            {validator.current.message(
              __t("validations.document.category"),
              values.category,
              "required",
              {
                messages: { required: __t("validations.document.category") },
              }
            )}
          </div>

          {isAdmin && (
            <div className="vp-up-field">
              <label className="vp-up-label" htmlFor="vp-doc-vendors">
                Visible To Vendors<span className="req">*</span>
              </label>
              <Select
                inputId="vp-doc-vendors"
                classNamePrefix="vp-share-sel"
                isMulti
                placeholder="All Vendors, or search for specific vendors…"
                options={ALL_VENDORS_OPTION.concat(
                  vendors.map((vendor) => ({
                    value: vendor.id,
                    label: vendor.name,
                  }))
                )}
                value={
                  allVendors
                    ? ALL_VENDORS_OPTION
                    : vendors
                        .filter((vendor) => pickedVendors.includes(vendor.id))
                        .map((vendor) => ({
                          value: vendor.id,
                          label: vendor.name,
                        }))
                }
                onChange={(chosen) => {
                  setVendorError("");
                  const list = chosen || [];
                  const hasAll = list.some((option) => option.value === "all");

                  if (hasAll && (!allVendors || list.length === 1)) {
                    setAllVendors(true);
                    setPickedVendors([]);
                  } else {
                    setAllVendors(false);
                    setPickedVendors(
                      list
                        .filter((option) => option.value !== "all")
                        .map((option) => option.value)
                    );
                  }
                }}
              />
              {vendorError && <div className="vp-up-err">{vendorError}</div>}
            </div>
          )}

          <div className="vp-up-field">
            <label className="vp-up-label" htmlFor="remark">
              Remark <span className="opt">(Optional)</span>
            </label>
            <div className="vp-up-cwrap">
              <Field
                as="textarea"
                id="remark"
                name="remark"
                rows={3}
                maxLength={MAX_REMARK}
                placeholder="Add a remark (optional)"
              />
              <span className="vp-up-count">
                {values.remark.length} / {MAX_REMARK}
              </span>
            </div>
          </div>

          <div className="vp-up-foot">
            <button
              type="button"
              className="vp-up-cancel"
              onClick={onCancel}
              disabled={uploading}
            >
              Cancel
            </button>
            <button type="submit" className="vp-up-submit" disabled={uploading}>
              <VendorUploadSvg size={15} />
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default UploadDocument;
